<template>
  <div class="themed-table-toolbar" role="toolbar" aria-label="Table toolbar">
    <!-- Bulk Actions Toolbar (shown when rows are selected) -->
    <div 
      v-if="showBulkActions && selectedCount > 0"
      class="themed-table-bulk-actions-toolbar"
      aria-label="Bulk actions toolbar"
      :aria-describedby="`bulk-actions-description-${_uid}`"
    >
      <div class="themed-table-bulk-actions-info">
        <span 
          class="themed-table-bulk-actions-count"
          :id="`bulk-actions-description-${_uid}`"
          role="status"
          aria-live="polite"
        >
          {{ selectedCount }} {{ selectedCount === 1 ? 'item' : 'items' }} selected
        </span>
      </div>
      
      <div class="themed-table-bulk-actions-buttons">
        <slot name="bulk-actions">
          <themed-select
            :value="selectedBulkAction"
            :options="bulkActionOptions"
            @input="handleBulkActionSelect"
            placeholder="Select action..."
            aria-label="Select bulk action"
            :disabled="disabled"
          />
          <themed-button
            variant="default"
            @click="executeBulkAction"
            :disabled="disabled || !selectedBulkAction"
            aria-label="Execute selected bulk action"
          >
            Apply
          </themed-button>
        </slot>
      </div>
    </div>

    <!-- Regular Toolbar -->
    <div v-if="!showBulkActions || selectedCount === 0" class="themed-table-toolbar-content">
      <!-- Title Section (optional slot) -->
      <div v-if="$slots.title || showTitle" class="themed-table-toolbar-title">
        <slot name="title">
          <h1 v-if="title" class="themed-table-toolbar-title-text">
            {{ title }}
            <span v-if="showCount" class="themed-table-toolbar-count">
              / {{ currentCount }} of {{ totalCount }}
            </span>
          </h1>
        </slot>
      </div>

      <!-- Main Controls -->
      <div class="themed-table-toolbar-controls">
        <!-- Search input -->
        <div 
          v-if="searchable"
          class="themed-table-toolbar-search"
          role="search"
        >
          <themed-search
            :id="searchInputId"
            :value="searchQuery"
            @input="handleSearchInput"
            placeholder="Search..."
            aria-label="Search table data"
            :aria-describedby="`${searchInputId}-description`"
            :disabled="disabled"
          />
          <span :id="`${searchInputId}-description`" class="sr-only">
            Type to search across table columns. Results will update automatically.
          </span>
        </div>

        <!-- Custom Controls Slot (for view toggle, custom filters, etc.) -->
        <slot name="custom-controls">
          <!-- View controls group (view mode + column visibility) -->
          <div v-if="showViewToggle || showColumnVisibility" class="themed-table-toolbar-view-controls">
            <!-- View Mode Toggle -->
            <view-mode-toggle
              v-if="showViewToggle"
              :value="viewMode"
              @input="handleViewModeChange"
            />
            
            <!-- Column visibility button (styled like view mode toggle) -->
            <column-visibility-dropdown
              v-if="showColumnVisibility"
              :columns="columns"
              :visible-columns="visibleColumns"
              :disabled="disabled"
              @column-toggle="handleColumnToggle"
            />
          </div>
          
          <!-- Sort/Filter Dropdown -->
          <sort-filter-dropdown
            v-if="showSortFilter"
            :sort-options="sortOptions"
            :sort-by="sortBy"
            :sort-direction="sortDirection"
            :filter-options="filterOptions"
            :disabled="disabled"
            @sort-change="handleSortChange"
            @sort-direction-change="handleSortDirectionChange"
            @filter-change="handleFilterChange"
            @clear-filters="handleClearFilters"
          />
        </slot>
        
        <!-- Per-page selector -->
        <div 
          v-if="showPerPageSelector"
          class="themed-table-toolbar-per-page"
          role="group"
          aria-label="Items per page selector"
        >
          <label 
            :for="perPageSelectId"
            class="themed-table-toolbar-label"
          >
            Show
          </label>
          <themed-select
            :id="perPageSelectId"
            :value="perPage"
            :options="perPageOptions"
            @input="handlePerPageChange"
            aria-label="Select number of items per page"
            :aria-describedby="`${perPageSelectId}-description`"
            :disabled="disabled"
          />
          <span :id="`${perPageSelectId}-description`" class="sr-only">
            Change the number of items displayed per page
          </span>
        </div>
        
        <!-- Action buttons -->
        <div class="themed-table-toolbar-actions">
          <!-- Custom Action Buttons Slot -->
          <slot name="action-buttons"></slot>

          <!-- Refresh button -->
          <themed-button
            v-if="showRefresh"
            variant="outline"
            size="icon"
            @click="handleRefresh"
            :disabled="disabled"
            aria-label="Refresh table data"
            title="Refresh"
          >
            <theme-icon icon="refresh" type="icons" classes="flex" />
          </themed-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSearch from '../ThemedSearch.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ViewModeToggle from '../ViewModeToggle.vue';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown.vue';
import SortFilterDropdown from './SortFilterDropdown.vue';

export default {
  name: 'TableToolbar',
  components: {
    ThemedSelect,
    ThemedButton,
    ThemedSearch,
    ThemeIcon,
    ViewModeToggle,
    ColumnVisibilityDropdown,
    SortFilterDropdown
  },
  props: {
    // Title configuration
    showTitle: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    showCount: {
      type: Boolean,
      default: false
    },
    currentCount: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    },
    
    // Search configuration
    searchable: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    
    // Per-page configuration
    perPageOptions: {
      type: Array,
      default: () => [5, 10, 20, 50, 100]
    },
    perPage: {
      type: Number,
      default: 10
    },
    showPerPageSelector: {
      type: Boolean,
      default: false
    },
    
    // View mode toggle configuration
    showViewToggle: {
      type: Boolean,
      default: false
    },
    viewMode: {
      type: String,
      default: 'grid',
      validator: (v) => ['grid', 'list'].includes(v)
    },
    
    // Sort/Filter configuration
    showSortFilter: {
      type: Boolean,
      default: false
    },
    sortOptions: {
      type: Array,
      default: () => []
      // Format: [{ label, value }]
    },
    sortBy: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: 'asc',
      validator: (v) => ['asc', 'desc'].includes(v)
    },
    filterOptions: {
      type: Array,
      default: () => []
      // Format: 
      // Toggle: [{ type: 'toggle', label, value, model }]
      // Select: [{ type: 'select', label, value, options: [{ label, value }], placeholder, model }]
    },
    
    // Bulk actions configuration
    bulkActions: {
      type: Array,
      default: null
      // Format: [{ name, label, icon, variant }]
    },
    selectedCount: {
      type: Number,
      default: 0
    },
    showBulkActions: {
      type: Boolean,
      default: false
    },
    
    // General configuration
    disabled: {
      type: Boolean,
      default: false
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    showRefresh: {
      type: Boolean,
      default: true
    },
    
    // Column visibility configuration
    showColumnVisibility: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array,
      default: null
    },
    visibleColumns: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      debounceTimer: null,
      selectedBulkAction: null
    };
  },
  computed: {
    perPageSelectId() {
      return `per-page-select-${this._uid}`;
    },
    searchInputId() {
      return `search-input-${this._uid}`;
    },
    bulkActionOptions() {
      if (!this.bulkActions) {
        return [];
      }
      return this.bulkActions.map(action => ({
        value: action.name,
        label: action.label
      }));
    }
  },
  methods: {
    handleSearchInput(value) {
      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Set new timer for 300ms debounce
      this.debounceTimer = setTimeout(() => {
        this.$emit('search-change', value);
      }, 300);
    },
    
    handlePerPageChange(value) {
      // Convert string to number
      const numValue = parseInt(value, 10);
      this.$emit('per-page-change', numValue);
    },
    
    handleBulkActionSelect(value) {
      this.selectedBulkAction = value;
    },
    
    executeBulkAction() {
      if (this.selectedBulkAction) {
        this.$emit('bulk-action', this.selectedBulkAction);
        // Reset selection after execution
        this.selectedBulkAction = null;
      }
    },
    
    handleClearSearch() {
      // ThemedSearch component handles clear internally
      this.$emit('search-change', '');
    },
    
    handleReset() {
      this.$emit('reset');
    },
    
    handleRefresh() {
      this.$emit('refresh');
    },
    
    handleColumnToggle(event) {
      this.$emit('column-toggle', event);
    },
    
    handleViewModeChange(value) {
      this.$emit('view-mode-change', value);
    },
    
    handleSortChange(value) {
      this.$emit('sort-change', value);
    },
    
    handleSortDirectionChange(value) {
      this.$emit('sort-direction-change', value);
    },
    
    handleFilterChange(event) {
      this.$emit('filter-change', event);
    },
    
    handleClearFilters() {
      this.$emit('clear-filters');
    }
  },
  beforeDestroy() {
    // Clean up timer on component destroy
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
</script>
